import type { ViewController } from '@presentation/shell/NexxusShell';
import {
  ECHEANCES_FISCALES_GABON,
  type EcheanceFiscaleGabon,
} from '@shared/constants/EcheancesFiscalesGabon';
import { esc, fmtDate } from '@presentation/shell/format';

interface EcheanceOccurrence {
  date: Date;
  echeance: EcheanceFiscaleGabon;
}

const MOIS_LABELS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

function kpi(label: string, value: string | number): string {
  return `<div class="kpi"><div class="label">${label}</div><div class="value">${value}</div></div>`;
}

function collectOccurrences(ref: Date, horizonDays: number): EcheanceOccurrence[] {
  const out: EcheanceOccurrence[] = [];
  for (let d = 0; d <= horizonDays; d++) {
    const dt = new Date(ref);
    dt.setDate(ref.getDate() + d);
    const year = dt.getFullYear();
    const month = dt.getMonth() + 1;
    const day = dt.getDate();
    for (const e of ECHEANCES_FISCALES_GABON) {
      if (e.mois_recurrent === 'mensuel' && day === e.jour) {
        out.push({ date: new Date(year, dt.getMonth(), day), echeance: e });
      } else if (e.mois === month && day === e.jour) {
        out.push({ date: new Date(year, dt.getMonth(), day), echeance: e });
      }
    }
  }
  return out.sort((a, b) => a.date.getTime() - b.date.getTime());
}

function echeancesForDay(month: number, day: number): EcheanceFiscaleGabon[] {
  return ECHEANCES_FISCALES_GABON.filter((e) => {
    if (e.mois_recurrent === 'mensuel' && e.jour === day) return true;
    return e.mois === month && e.jour === day;
  });
}

export const echeancesFiscalesController: ViewController = async (section) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const futures = collectOccurrences(now, 90).slice(0, 24);
  const urgent = futures.filter((f) => {
    const diff = Math.floor((f.date.getTime() - now.getTime()) / 86_400_000);
    return diff <= 7;
  }).length;

  const grid = section.querySelector('#ecf-kpi-grid');
  if (grid) {
    grid.innerHTML =
      kpi('Échéances (90 j)', futures.length) + kpi('Sous 7 jours', urgent) + kpi('Exercice', year);
  }

  const tbody = section.querySelector('#ecf-upcoming-table tbody');
  if (tbody) {
    tbody.innerHTML =
      futures.length === 0
        ? '<tr><td colspan="4" style="color:var(--gris);padding:14px;">Aucune échéance dans les 90 prochains jours.</td></tr>'
        : futures
            .map((f) => {
              const diff = Math.floor((f.date.getTime() - now.getTime()) / 86_400_000);
              const urgentRow = diff <= 7 ? ' style="background:var(--accent-soft, #fef2f2)"' : '';
              const when = diff === 0 ? "aujourd'hui" : diff > 0 ? `J+${diff}` : `J${diff}`;
              return `<tr${urgentRow}>
          <td>${esc(fmtDate(f.date.toISOString().slice(0, 10)))} <span style="color:var(--gris);font-size:12px;">(${esc(when)})</span></td>
          <td class="mono"><strong>${esc(f.echeance.code)}</strong></td>
          <td>${esc(f.echeance.label)}</td>
          <td style="font-size:12px;color:var(--gris);">${esc(f.echeance.source)}</td>
        </tr>`;
            })
            .join('');
  }

  const cal = section.querySelector('#ecf-calendar-grid');
  if (cal) {
    const firstDay = new Date(year, month, 1).getDay() || 7;
    const lastDay = new Date(year, month + 1, 0).getDate();
    let html = '';
    for (const j of ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']) {
      html += `<div style="text-align:center;font-weight:600;color:var(--gris);font-size:12px;padding:4px;">${j}</div>`;
    }
    for (let i = 1; i < firstDay; i++) html += '<div></div>';
    for (let d = 1; d <= lastDay; d++) {
      const ech = echeancesForDay(month + 1, d);
      const isToday = d === now.getDate() && month === now.getMonth();
      const border = isToday
        ? 'border:2px solid var(--teal);'
        : 'border:1px solid var(--border, #DAE0E4);';
      html += `<div style="min-height:52px;padding:4px;border-radius:6px;${border}background:var(--white);">
        <div style="font-weight:${isToday ? '700' : '500'};font-size:12px;">${d}</div>`;
      for (const e of ech) {
        html += `<div style="font-size:10px;color:var(--teal);margin-top:2px;">${esc(e.code)}</div>`;
      }
      html += '</div>';
    }
    cal.innerHTML = html;
  }

  const title = section.querySelector('#ecf-month-title');
  if (title) title.textContent = `${MOIS_LABELS[month]} ${year}`;
};
